import axios from 'axios';
import { getStoredToken } from '../contexts/AuthContext';

const API_BASE_URL = (() => {
  const url = import.meta.env.VITE_API_URL as string | undefined;
  if (url) return url;
  if (import.meta.env.PROD) {
    throw new Error('VITE_API_URL is required for production builds');
  }
  return 'http://localhost:8083';
})();

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes for large file uploads
});

// Attach the JWT (if present) to every request. Backend ignores it in dev
// while the auth middleware is in shadow mode; once enforced, all calls
// past `/api/auth/*` will require it.
api.interceptors.request.use((cfg) => {
  const t = getStoredToken();
  if (t && cfg.headers) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export interface UploadResponse {
  audioFileId: string;
  filename: string;
  fileSize: number;
  status: string;
  message: string;
  fileUrl?: string;
}

export interface AnalyzeResponse {
  analysisId: string;
  audioFileId: string;
  status: string;
  message: string;
}

export interface ExperimentalFeatures {
  embedding_v1?: number[];
  embedding_version?: string;
  embedding_method?: string;
  embedding_dim?: number;
  torchaudio_error?: string;
  audioflux_feature_set_v1?: {
    spectral_centroid_mean?: number;
    spectral_centroid_std?: number;
    spectral_rolloff_mean?: number;
    spectral_rolloff_std?: number;
    spectral_flux_mean?: number;
    spectral_flux_std?: number;
    zcr_mean?: number;
    zcr_std?: number;
    spectral_bandwidth_mean?: number;
    spectral_bandwidth_std?: number;
    spectral_flatness_mean?: number;
    spectral_flatness_std?: number;
    mfcc?: number[];
    chroma?: number[];
  };
  audioflux_version?: string;
  audioflux_error?: string;
  sample_rate?: number;
  [key: string]: any; // Allow additional experimental fields
}

export type GenreFamily =
  | 'electronic'
  | 'pop'
  | 'hiphop'
  | 'rock'
  | 'jazz'
  | 'ambient'
  | 'unknown';

export interface GenrePrediction {
  primary: string;
  family: GenreFamily;
  confidence: number;
  reasoning: string;
  alternatives: Array<{
    genre: string;
    family: GenreFamily;
    confidence: number;
  }>;
}

export interface AnalysisResult {
  id: string;
  audioFileId: string;
  status: string;
  tempo?: number;
  key?: string;
  chordProgression?: string[];
  danceability?: number;
  loudness?: {
    integratedLufs: number;
    range?: number;
  };
  dynamicRange?: {
    complexity: number;
  };
  spectral?: {
    centroid: number;
    rolloff?: number;
  };
  experimental?: ExperimentalFeatures;
  /** Worker-side genre classification (BPM-primary). Always present once analysis completes. */
  genre?: GenrePrediction | null;
  timeSignature?: string;
  durationSeconds?: number;
  segments: Segment[];
  createdAt: string;
  completedAt?: string;
  fileUrl?: string;
  filename?: string;
}

export interface Instrument {
  type: string;
  confidence: number;
}

export interface Mixing {
  volumeDb: number;
  panPosition: number;
  frequencySpectrum: Record<string, number>;
  dynamicRange: number;
  compressionRatio: number;
}

export interface Segment {
  id: string;
  segmentNumber: number;
  startTime: number;
  endTime: number;
  startTimeSeconds?: number;
  endTimeSeconds?: number;
  barsCount: number;
  chordProgression: string[];
  key?: string;
  energy?: number;
  instruments?: Instrument[];
  mixing?: Mixing | null;
}

export interface AnalysisStatus {
  status: string;
  progress: number;
  analysisId: string;
}

/**
 * Upload an audio file. Two paths:
 *
 *  - PROD (S3 + Lambda): API issues a pre-signed PUT URL; we PUT the file
 *    directly to S3, then call /upload-finalize so the API records the
 *    `audio_files` row. Avoids API Gateway's 6 MB body cap.
 *
 *  - LOCAL DEV (no S3): falls back to legacy multipart POST /upload, which
 *    streams the file straight to the local filesystem.
 *
 * The two paths share the same `UploadResponse` shape so callers don't care.
 */
export async function uploadFile(
  file: File,
  segmentSize: 4 | 8 = 8,
  onProgress?: (progress: number) => void
): Promise<UploadResponse> {
  // Try the presigned path first.
  try {
    const presign = await api.post<{
      audioFileId: string;
      s3Key: string;
      uploadUrl: string;
      fileUrl: string;
      expiresIn: number;
    }>('/upload-url', { filename: file.name, contentType: file.type || undefined });

    // Upload directly to S3. axios's `Authorization` interceptor would
    // break the pre-signed signature, so we use a fresh axios call with
    // headers we own.
    await axios.put(presign.data.uploadUrl, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });

    const finalize = await api.post<UploadResponse>('/upload-finalize', {
      audioFileId: presign.data.audioFileId,
      filename:    file.name,
      fileSize:    file.size,
      s3Key:       presign.data.s3Key,
    });
    return finalize.data;
  } catch (err: any) {
    // Server says S3 isn't configured (local dev) — fall through to multipart.
    const msg = err?.response?.data?.error || '';
    if (err?.response?.status !== 400 || !/multipart|S3|upload/i.test(msg)) {
      // The presigned path failed for some non-fallback reason.
      // Re-throw unless the server explicitly told us to use /upload.
      if (err?.response?.status !== 404 && err?.response?.status !== 400) throw err;
    }
  }

  // Legacy multipart upload — local dev fallback.
  const formData = new FormData();
  formData.append('file', file);
  formData.append('segmentSize', segmentSize.toString());

  const response = await api.post<UploadResponse>('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      }
    },
  });
  return response.data;
}

export async function analyzeAudio(audioFileId: string, segmentSize: 4 | 8 = 8, features: string[] = ['chords', 'tempo', 'key']): Promise<AnalyzeResponse> {
  const response = await api.post<AnalyzeResponse>('/analyze', {
    audioFileId,
    segmentSize,
    features,
  });

  return response.data;
}

export async function getAnalysis(analysisId: string): Promise<AnalysisResult> {
  const response = await api.get<AnalysisResult>(`/analysis/${analysisId}`);
  return response.data;
}

export async function getAnalysisStatus(analysisId: string): Promise<AnalysisStatus> {
  const response = await api.get<AnalysisStatus>(`/analysis/${analysisId}/status`);
  return response.data;
}

export interface AnalysisHistoryItem {
  analysisId: string;
  filename: string;
  tempo?: number;
  key?: string;
  durationSeconds?: number;
  createdAt: string;
  completedAt?: string;
  status: string;
}

export async function getHistory(): Promise<AnalysisHistoryItem[]> {
  const response = await api.get<{ analyses: AnalysisHistoryItem[] }>('/history');
  return response.data.analyses;
}

export interface ExtractSampleResponse {
  success: boolean;
  sampleUrl: string;
  filename: string;
  startTime: number;
  endTime: number;
  duration: number;
}

export async function extractSample(
  analysisId: string,
  startTime: number,
  endTime: number,
  sectionType?: string
): Promise<ExtractSampleResponse> {
  const response = await api.post<ExtractSampleResponse>('/sample/extract', {
    analysisId,
    startTime,
    endTime,
    sectionType,
  });
  return response.data;
}

export interface AIChatRequest {
  message: string;
  analysis: AnalysisResult;
  sections: any[];
  conversationHistory?: Array<{ role: string; content: string }>;
}

export interface AIChatResponse {
  response: string;
}

export async function sendAIChatMessage(request: AIChatRequest): Promise<AIChatResponse> {
  const response = await api.post<AIChatResponse>('/ai/chat', request);
  return response.data;
}

// ───────────────────────── Section overrides ─────────────────────────

export interface OverrideRecord {
  analysisId: string;
  action?: 'update' | 'insert';
  ts: string;
  /** update path */
  sectionIndex?: number;
  originalType?: string;
  originalStartTime?: number;
  originalEndTime?: number;
  /** insert path */
  insertId?: string;
  /** corrected payload (shared) */
  correctedType?: string;
  correctedStartTime?: number;
  correctedEndTime?: number;
}

export interface OverridesResponse {
  analysisId: string;
  /** Latest correction per sectionIndex (alias of `updates`, kept for back-compat). */
  overrides: Record<string, OverrideRecord>;
  /** Latest correction per sectionIndex. */
  updates: Record<string, OverrideRecord>;
  /** Latest insert per insertId. */
  inserts: Record<string, OverrideRecord>;
  /** Full append-only history for this analysis. */
  history: OverrideRecord[];
}

export async function getOverrides(analysisId: string): Promise<OverridesResponse> {
  const response = await api.get<OverridesResponse>(`/overrides/${analysisId}`);
  return response.data;
}

export async function clearOverrides(analysisId: string): Promise<{ ok: boolean }> {
  const response = await api.delete<{ ok: boolean }>(`/overrides/${analysisId}`);
  return response.data;
}

export async function recordOverride(
  payload: {
    analysisId: string;
    action?: 'update' | 'insert';
    sectionIndex?: number;
    originalType?: string;
    originalStartTime?: number;
    originalEndTime?: number;
    insertId?: string;
    deleted?: boolean;
    correctedType?: string;
    correctedStartTime?: number;
    correctedEndTime?: number;
  },
): Promise<{ ok: boolean; record: OverrideRecord }> {
  const response = await api.post<{ ok: boolean; record: OverrideRecord }>(
    '/overrides',
    payload,
  );
  return response.data;
}